import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import i18n from 'simple-react-i18n'
import ProgressCard from '../../../../components/card/ProgressCard'
import Input from '../../../../components/forms/Input'
import RadioButtons from '../../../../components/forms/RadioButtons'
import Select from '../../../../components/forms/Select'
import QualityAction from '../../../../quality/actions/QualityAction'
import CityAction from '../../../../referencial/components/city/actions/CityAction'
import ContributorAction from '../../../../referencial/components/contributor/actions/ContributorAction'
import NetworkAction from '../../../../referencial/components/network/actions/NetworkAction'
import { nbPerPageLabelShort } from '../../../../referencial/constants/ReferencialConstants'
import Job from '../../../dto/DtoJob'
import Checkbox from '../../../../components/forms/Checkbox'
import CampaignAction from '../../../../campaign/actions/CampaignAction'
import SuperMultiAutocomplete from '../../../../components/forms/SuperMultiAutocomplete'
import { Box, Grid } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import { mainBlue } from 'utils/constants/ColorTheme'
import { DENSE_INPUT_SX } from 'utils/constants/Theme'
import { getStationModes } from 'utils/JobUtils'
import SelectionTableModal from 'components/modal/SelectionTableModal'
import { QualitometerFilterField } from 'components/datatable/SimpleSelectionTable'
import { normalizeString } from 'utils/StringUtil'
import { intersectionBy, isArray, uniq } from 'lodash'
import useParseJson from 'utils/customHook/useParseJson'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { QUALITY } from 'administration/components/user/constants/StatisticConstants'

const ExportQesoutPanelQualite = ({
    job,
    onChangeJob,
    isEditMode,
}) => {
    const dispatch = useDispatch()
    const [isOpen, setIsOpen] = useState()

    const {
        qualitometers,
        qualitosIndex,
        contributors,
        networks,
        cities,
        qualityCampaigns,
        producers,
        citiesIndex,
        stationsEsu,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        qualitosIndex: store.QualityReducer.qualitosIndex,
        contributors: store.ContributorReducer.contributors,
        networks: store.NetworkReducer.networks,
        cities: store.CityReducer.cities,
        citiesIndex: store.CityReducer.citiesIndex,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        producers: store.ContributorReducer.producers,
        stationsEsu: store.QualityReducer.stationsEsu,
    }))

    useEffect(() => {
        if (!qualitometers.length) {
            dispatch(QualityAction.fetchQualitometersLight())
        }
        if (!contributors.length) {
            dispatch(ContributorAction.fetchContributors())
        }
        if (!networks.length) {
            dispatch(NetworkAction.fetchNetworks())
        }
        if (!cities.length) {
            dispatch(CityAction.fetchCities())
        }
        if (!qualityCampaigns.length) {
            dispatch(CampaignAction.fetchQualityCampaigns())
        }
        if (!producers.length) {
            dispatch(ContributorAction.fetchProducers())
        }
        if (!stationsEsu.length) {
            dispatch(QualityAction.fetchEsuLight())
        }
    }, [contributors.length, networks.length, cities.length, qualityCampaigns.length, producers.length, qualitometers.length, stationsEsu.length])

    const onChangeJobParameter = (changes) => {
        const jobParameters = {
            ...job.parameters,
            ...changes,
        }
        onChangeJob({ parameters: jobParameters })
    }

    const {
        json: filters,
    } = useParseJson(job.parameters.filters[0] ?? '{}')

    const onChangeFilters = (changes) => {
        const parameters = {
            ...job.parameters,
            filters: [JSON.stringify({
                ...filters,
                ...changes,
            })],
        }
        onChangeJob({ parameters })
    }

    const contributorsFormatted = contributors.filter(c => c.siret?.length > 0).map((c) => ({
        ...c,
        nameFormatted: `${c?.siret} - ${c?.name || ''}`,
    }))

    const disabled = !isEditMode
    const exportModes = [
        { code: 'ALL', name: i18n.exportHistory },
        { code: 'CUSTOM', name: i18n.choiseDate },
        { code: 'DIFF', name: i18n.exportFromLastExport },
    ]

    const selectedStations = filters?.stations?.map(s => {
        const station = qualitosIndex[s]
        return { ...station, city: citiesIndex[station?.townCode]?.name }
    })

    const internalReferences = useMemo(() => isArray(filters.withInternalReference) ? filters.withInternalReference : [], [filters.withInternalReference])

    const onChangeStationCode = (station, code) => onChangeFilters({
        withInternalReference: [
            ...internalReferences.filter(c => c.idStation !== station.id),
            ...(code && code !== station.code ? [{ idStation: station.id, code }] : []),
        ],
    })

    const stationCodesByStation = useMemo(() => stationsEsu.reduce((codes, station) => {
        if (!station.codequalitometre || !station.codebss) {
            return codes
        }
        codes[station.codequalitometre] = [...(codes[station.codequalitometre] || []), station.codebss]
        return codes
    }, {}), [stationsEsu])

    const getCodeCell = station => {
        const codes = stationCodesByStation[station.code] || []
        if (!codes.length) {
            return station.code
        }
        const options = uniq([station.code, ...codes]).map(code => ({ code, name: code }))
        return (
            <Box sx={DENSE_INPUT_SX}>
                <Select
                    value={internalReferences.find(c => c.idStation === station.id)?.code ?? station.code}
                    options={options}
                    keyValue='code'
                    keyLabel='name'
                    noNullValue
                    onChange={v => onChangeStationCode(station, v)}
                    disabled={disabled}
                />
            </Box>
        )
    }

    return (
        <>
            {qualitometers.length &&
                <Grid container columnSpacing={2} sx={{ padding: '10px' }} alignItems='center'>
                    <Grid size={2}>
                        <Input
                            title={i18n.directory}
                            value={job.parameters.path}
                            onChange={v => onChangeJobParameter({ path: v })}
                            disabled={disabled}
                            obligatory
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            options={exportModes}
                            label={i18n.exportMode}
                            onChange={v => onChangeFilters({ mode: v })}
                            value={filters.mode}
                            disabled={disabled}
                            obligatory
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.campaign}
                            options={qualityCampaigns}
                            values={filters.campaignIds}
                            onChange={v => onChangeFilters({ campaignIds: v })}
                            multiple

                            disabled={disabled}
                        />
                    </Grid>
                    <Grid size={2}>
                        {
                            filters.mode === 'CUSTOM' && (
                                <SimpleDatePicker
                                    label={i18n.startDate}
                                    value={filters.startDate}
                                    onChange={(v) => onChangeFilters({ startDate: v })}
                                    disabled={disabled}
                                />
                            )
                        }
                    </Grid>
                    <Grid size={2}>
                        {
                            filters.mode === 'CUSTOM' && (
                                <SimpleDatePicker
                                    label={i18n.endDate}
                                    value={filters.endDate}
                                    onChange={(v) => onChangeFilters({ endDate: v })}
                                    disabled={disabled}
                                />
                            )
                        }
                    </Grid>
                    <Grid size={4}>
                        <MultiContributorsAutocomplete
                            label={i18n.sender}
                            values={filters.siret}
                            onChange={c => onChangeFilters({ siret: c })}
                            options={contributorsFormatted}
                            keyLabel='nameFormatted'
                            keyValue='siret'
                            disabled={disabled}
                            obligatory
                        />
                    </Grid>
                    <Grid size={4}>
                        <MultiContributorsAutocomplete
                            options={producers}
                            label={i18n.producer}
                            values={filters.producer}
                            onChange={c => onChangeFilters({ producer: c })}
                            keyLabel='name'
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Checkbox
                            label={i18n.bruteMeasuresExport}
                            checked={filters.bruteMode}
                            onChange={c => onChangeFilters({ bruteMode: c })}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid size={12} container sx={{ padding: '10px 0' }}>
                        <Grid size={4} >
                            <RadioButtons
                                title={i18n.selectStations}
                                obligatory
                                elements={getStationModes()}
                                selected={filters.selectStationsMode || 'FILTERS'}
                                onChange={v => onChangeFilters({ selectStationsMode: v })}
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                    {
                        !filters.selectStationsMode || filters.selectStationsMode === 'FILTERS' ? (
                            <>
                                <Grid size={4}>
                                    <Select
                                        options={networks}
                                        label={i18n.network}
                                        value={filters.network}
                                        onChange={c => onChangeFilters({ network: c })}
                                        disabled={disabled}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        options={cities}
                                        label={i18n.city}
                                        value={filters.city}
                                        onChange={c => onChangeFilters({ city: c })}
                                        disabled={disabled}
                                        keyLabel='labelWithCode'
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <SimpleFilterSelect
                                        onChange={(_, f) => onChangeFilters({ stationsFilter: f && f !== -1 ? f : null })}
                                        stations={qualitometers}
                                        stationType={QUALITY}
                                        disabled={disabled}
                                        value={filters.stationsFilter}
                                        ignoreCache
                                    />
                                </Grid>
                            </>
                        ) : (
                            <Grid size={12}>
                                <CardTable
                                    title={i18n.stations}
                                    rows={(selectedStations || []).map(station => ({
                                        ...station,
                                        code: getCodeCell(station),
                                    }))}
                                    rowsPerPageOptions={nbPerPageLabelShort}
                                    headers={['code', 'name', 'city']}
                                    actions={[{ icon: 'edit', onClick: setIsOpen, color: 'white' }]}
                                    color={mainBlue}
                                />
                            </Grid>
                        )
                    }
                </Grid> || <ProgressCard progress={50} />
            }

            <SelectionTableModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={i18n.stations}
                onValidate={idsStations => {
                    onChangeFilters({
                        stations: idsStations,
                        withInternalReference: internalReferences.filter(c => idsStations.includes(c.idStation)),
                    })
                    setIsOpen(false)
                }}

                listData={qualitometers.map(q => ({ ...q, city: citiesIndex[q.townCode]?.labelWithCode ?? '' }))}
                defaultSelectionList={filters?.stations ?? []}

                listHeaders={['code', 'name', 'city']}
                listTitle={i18n.nonSelectedStations}
                selectionListTitle={i18n.selectedStations}

                filterField={QualitometerFilterField}
                filterFunction={(list, filterValues) => {
                    const { searchValue, city, filter, resultFilter } = filterValues
                    const searchValueFormat = normalizeString(searchValue)
                    const filter1 = searchValue ? list.filter(p => normalizeString([p.code, p.name].join('   ')).includes(searchValueFormat)) : list
                    const filter2 = city ? filter1.filter(q => q.townCode === city) : filter1
                    return filter && filter !== -1 ? intersectionBy(filter2, resultFilter, 'id') : filter2
                }}
            />
        </>
    )
}

ExportQesoutPanelQualite.propTypes = {
    job: PropTypes.instanceOf(Job).isRequired,
    onChangeJob: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
}

export default ExportQesoutPanelQualite